if (!customElements.get('new-in-deck')) {
  class NewInDeck extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.nid-viewport');
      this.cards = Array.from(this.querySelectorAll('.nid-card'));
      this.fill = this.querySelector('.nid-progress__fill');
      this.prevBtn = this.querySelector('.nid-nav--prev');
      this.nextBtn = this.querySelector('.nid-nav--next');
      this.counter = this.querySelector('.nid-counter__cur');
      this.count = this.cards.length;
      this.current = 0;
      if (!this.viewport || this.count === 0) return;

      // Vehicle filter ("Trouver ma BMW"): keep every card, show the matches
      this.allCards = [...this.cards];
      this.section = this.closest('[data-nid-section]');
      this.heading = this.section?.querySelector('.nid-section__heading');
      this.headingText = this.heading?.textContent || '';
      this.emptyBox = this.querySelector('[data-nid-empty]');
      this.foot = this.querySelector('.nid-foot');
      document.addEventListener('gr:vehicle-filter', (event) => this.applyFilter(event.detail));

      this.userEngaged = false;

      this.render();
      this.bindDrag();
      // Re-lay the deck when crossing the phone/desktop breakpoint
      this.mq = window.matchMedia('(max-width: 949px)');
      this.mq.addEventListener('change', () => this.render());

      if (this.prevBtn)
        this.prevBtn.addEventListener('click', () => {
          this.userEngaged = true;
          this.go(-1);
        });
      if (this.nextBtn)
        this.nextBtn.addEventListener('click', () => {
          this.userEngaged = true;
          this.go(1);
        });

      this.addEventListener('dragstart', (event) => event.preventDefault());

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.userEngaged = true;
          this.go(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.userEngaged = true;
          this.go(-1);
        }
      });

      this.initHintOnce();

      this.cards.forEach((card) => {
        card.addEventListener('click', (event) => {
          if (card.dataset.state === '0' || this.moved) return;
          if (event.target.closest('a')) return;
          event.preventDefault();
          this.userEngaged = true;
          this.setIndex(this.cards.indexOf(card));
        });
      });

      // A vehicle may already be chosen (restored by the finder before this loaded)
      if (window.__grVehicleFilter) this.applyFilter(window.__grVehicleFilter);
    }

    /** @param {{match: string[], label: string} | null} detail */
    applyFilter(detail) {
      const match = detail && Array.isArray(detail.match) ? detail.match : null;
      this.allCards.forEach((card) => {
        let chassis = [];
        try {
          chassis = JSON.parse(card.dataset.chassis || '[]') || [];
        } catch (e) {}
        card.hidden = Boolean(match) && !chassis.some((c) => match.includes(c));
      });
      this.cards = this.allCards.filter((card) => !card.hidden);
      this.count = this.cards.length;
      this.current = 0;

      // renumber + re-alternate the lime cards
      this.cards.forEach((card, i) => {
        const index = card.querySelector('.nid-card__index');
        if (index) index.textContent = String(i + 1);
        card.classList.toggle('nid-card--accent', i % 2 === 1);
      });

      if (this.heading) {
        this.heading.textContent = match
          ? `${this.count} volant${this.count > 1 ? 's' : ''} compatible${this.count > 1 ? 's' : ''} avec votre ${detail.label}`
          : this.headingText;
      }

      const none = Boolean(match) && this.count === 0;
      this.viewport.hidden = none;
      if (this.emptyBox) this.emptyBox.hidden = !none;
      if (this.foot) this.foot.hidden = none || this.count < 2;
      if (!none) this.render();
    }

    go(direction) {
      this.setIndex(this.current + direction);
    }

    /* One-time cue: ~2s after the deck first scrolls into view, glide to the
       next card so the reader notices it can be paged / swiped. Fires once ever
       (remembered in the browser), and never if the reader already took over. */
    initHintOnce() {
      if (this.count < 2) return;
      const KEY = 'gr-newin-hint-seen';
      try {
        if (localStorage.getItem(KEY)) return;
      } catch (e) {
        /* storage blocked — fall through, it just won't be remembered */
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const scroller = document.querySelector('.page-wrapper');
      let timer = null;
      let done = false;

      const inView = () => {
        const r = this.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        return r.top < vh * 0.75 && r.bottom > vh * 0.3;
      };

      const cleanup = () => {
        if (timer) clearTimeout(timer);
        timer = null;
        if (scroller) scroller.removeEventListener('scroll', check);
        window.removeEventListener('scroll', check);
        window.removeEventListener('resize', check);
      };

      const fire = () => {
        timer = null;
        if (done) return;
        done = true;
        try {
          localStorage.setItem(KEY, '1');
        } catch (e) {
          /* ignore */
        }
        cleanup();
        if (!this.userEngaged && this.current === 0) this.go(1);
      };

      const check = () => {
        if (done) return;
        if (this.userEngaged) {
          done = true;
          cleanup();
          return;
        }
        if (inView()) {
          if (!timer) timer = setTimeout(fire, 2000);
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      };

      if (scroller) scroller.addEventListener('scroll', check, { passive: true });
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check, { passive: true });
      check();
      requestAnimationFrame(check);
      setTimeout(check, 600);
    }

    setIndex(index) {
      this.current = Math.max(0, Math.min(this.count - 1, index));
      this.render();
    }

    render(dragDx) {
      const drag = dragDx || 0;
      // Deck spacing per breakpoint, from CSS: --nid-step = how many px of each
      // card behind the front one stay visible, --nid-y / --nid-rot = drop and
      // tilt per layer, --nid-depth = layers shown on each side.
      const css = getComputedStyle(this);
      const step = parseFloat(css.getPropertyValue('--nid-step')) || 60;
      const yStep = parseFloat(css.getPropertyValue('--nid-y')) || 6;
      const rotStep = parseFloat(css.getPropertyValue('--nid-rot')) || 1.2;
      const depth = parseInt(css.getPropertyValue('--nid-depth'), 10) || 3;
      // Cards scale from their centre: push each layer out by the width it
      // loses so the visible strip stays `step` px per layer.
      const width = (this.cards[0] && this.cards[0].offsetWidth) || 0;
      this.cards.forEach((card, index) => {
        const offset = index - this.current;
        const layer = Math.abs(offset);
        if (layer > depth) {
          // beyond the stack: tuck behind the last layer on its side, invisible
          const side = offset < 0 ? -1 : 1;
          const scale = 1 - depth * 0.04;
          const x = side * (depth * step + ((1 - scale) * width) / 2);
          card.dataset.state = offset < 0 ? 'past' : 'hidden';
          card.style.transform =
            'translate(' + x + 'px, ' + depth * yStep + 'px) scale(' + scale + ') rotate(' + side * depth * rotStep + 'deg)';
          card.style.opacity = '0';
          card.style.filter = '';
          card.style.pointerEvents = 'none';
          card.style.zIndex = '0';
          return;
        }
        const side = offset < 0 ? -1 : 1;
        const scale = 1 - layer * 0.04;
        const x = side * (layer * step + ((1 - scale) * width) / 2) + (offset === 0 ? drag : 0);
        const y = layer * yStep;
        const rot = side * layer * rotStep;
        card.dataset.state = offset < 0 ? 'past-' + layer : String(offset);
        card.style.transform =
          'translate(' + x + 'px, ' + y + 'px) scale(' + scale + ') rotate(' + rot + 'deg)';
        if (offset < 0) {
          // seen cards (left): darker than the upcoming ones
          card.style.opacity = layer >= 3 ? '0.35' : layer === 2 ? '0.6' : '1';
          card.style.filter = 'brightness(' + (layer === 1 ? 0.5 : 0.42) + ')';
        } else {
          card.style.opacity = layer >= 3 ? '0.45' : layer === 2 ? '0.7' : '1';
          card.style.filter = '';
        }
        card.style.pointerEvents = offset === 0 ? 'auto' : 'none';
        card.style.zIndex = String(20 - layer);
      });

      if (this.fill) {
        const pct = this.count > 1 ? (this.current / (this.count - 1)) * 100 : 100;
        // scaleX instead of width: composited, no relayout per frame
        this.fill.style.transform = 'scaleX(' + Math.max(9, pct) / 100 + ')';
      }

      if (this.counter) this.counter.textContent = String(this.current + 1);
      if (this.prevBtn) this.prevBtn.disabled = this.current === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.current === this.count - 1;
    }

    bindDrag() {
      let startX = 0;
      let dragging = false;
      this.moved = false;

      const point = (event) => (event.touches ? event.touches[0].clientX : event.clientX);

      const down = (event) => {
        if (event.target.closest('a, button')) return;
        if (event.type === 'mousedown') event.preventDefault();
        this.userEngaged = true;
        dragging = true;
        this.moved = false;
        startX = point(event);
        this.classList.add('is-dragging');
        if (typeof this.focus === 'function') this.focus({ preventScroll: true });
      };

      const moveHandler = (event) => {
        if (!dragging) return;
        const dx = (event.changedTouches ? event.changedTouches[0].clientX : point(event)) - startX;
        if (Math.abs(dx) > 4) this.moved = true;
        this.render(dx * 0.5);
      };

      const up = (event) => {
        if (!dragging) return;
        dragging = false;
        this.classList.remove('is-dragging');
        const endX = event.changedTouches ? event.changedTouches[0].clientX : point(event);
        const dx = endX - startX;
        const threshold = 55;
        if (dx < -threshold) this.go(1);
        else if (dx > threshold) this.go(-1);
        else this.render(0);
      };

      this.viewport.addEventListener('mousedown', down);
      window.addEventListener('mousemove', moveHandler);
      window.addEventListener('mouseup', up);
      this.viewport.addEventListener('touchstart', down, { passive: true });
      this.viewport.addEventListener('touchmove', moveHandler, { passive: true });
      this.viewport.addEventListener('touchend', up);
    }
  }

  customElements.define('new-in-deck', NewInDeck);
}
